<template>
    <div class="w-full">
        <div class="w-full flex flex-column relative mb-3">
            <Message severity="secondary" icon="pi pi-lightbulb" class="border-round-2xl">
                <span class="line-height-2 text-sm xl:text-md lg:text-sm">
                    {{ content[lang].inline_msg_info }}
                </span>
            </Message>
        </div>  
        
        <!--AMOUNT SEND-->
        <span class="text-color-secondary">{{ content[lang].amount_title }}:</span>
        <div class="w-full mt-2 mb-3">
            <InputAmount v-if="currencies" :amount="amount_send" :currency="currency_send" :currencies="currencies" :min="1" :max="9999999999"
                @update:amount="val => amount_send = val" @update:currency="val => currency_send = val" 
                :disableInput="false" :disableSelect="false" digits="2" :isClearButtons="false" :isRequest="false" :isSearchable="true"/>
        </div>

        <!--AMOUNT RECEIVE-->
        <span class="text-color-secondary">{{ content[lang].currency_receive_title }}: </span>
        <div class="w-full flex flex-row mt-2 mb-3 p-inputgroup">
            <div class="flex w-full p-0 border-none surface-border border-round-xl align-items-center justify-content-end">
                <Button v-if="currency_receive" @click="popup=true" :label="currency_receive.code" severity="info" outlined :disabled="disabled"
                    class="flex w-full border-none border-300 border-round-2xl text-600 font-medium uppercase pl-1" 
                    style="min-width: 4rem;border-radius:100px!important;"
                    :class="(disabled) ? 'surface-0' : 'surface-50'">
                        <AvatarGroup v-if="currency_receive.image">
                            <Avatar :image="currency_receive.image" shape="circle" class="mr-1 border-none"/>
                        </AvatarGroup>
                        <i v-else class="pi pi-question-circle mr-1"></i>
                        <span class="uppercase">{{ currency_receive.code }}</span>
                        <i class="pi pi-chevron-right"></i>
                </Button>
            </div>
        </div>

        <div v-if="!isAuth" class="w-full flex flex-column">
            <span class="flex text-color-secondary mb-2">
                {{ content[lang].contact }}:
            </span>

            <SelectButton v-model="contact" :options="typesContact" aria-labelledby="basic" optionLabel="label" class="flex">
                <template #option="slotProps">
                    <div class="flex align-items-center text-xs">
                        <i :class="slotProps.option.icon"></i>
                        <span class="ml-2">{{ slotProps.option.label }}</span>
                    </div>
                </template>
            </SelectButton>

            <!--PHONE_NUMBER-->
            <div v-if="showWa" class="field my-3">
                <div class="p-inputgroup w-full mb-2 phone_drop flex flex-row">

                    <!--COUNTRIES-->
                    <span class="p-inputgroup-addon p-0 border-y-2 border-left-2 border-right-none surface-border flex">
                        <Dropdown v-model="selectedCountry" :options="countries" filter optionLabel="name" :placeholder="content[lang].country_placeholder"
                            class="w-full border-none border-round-left-lg p-inputtext-lg">
                            <template #value="slotProps">
                                <div v-if="slotProps.value" class="flex h-full max-w-6rem align-items-center">
                                    <img :alt="slotProps.value.label" src="/images/flags/flag_placeholder.png" :class="`mr-2 flag flag-${slotProps.value.code.toLowerCase()}`" style="width: 18px" />
                                    <div>{{ slotProps.value.first }}</div>
                                </div>
                                <span v-else>
                                {{ slotProps.placeholder }}
                                </span>
                            </template>
                            <template #option="slotProps">
                                <div class="flex max-w-6rem align-items-center">
                                    <img :alt="slotProps.option.label" src="/images/flags/flag_placeholder.png" :class="`mr-2 flag flag-${slotProps.option.code.toLowerCase()}`" style="width: 18px" />
                                    <div>{{ slotProps.option.name }} {{ slotProps.option.first }}</div>
                                </div>
                            </template>
                        </Dropdown>
                    </span>

                    <!--PHONE-->
                    <div class="p-inputgroup-addon flex flex-1 p-0 border-none surface-0">
                        <IconField iconPosition="right" class="w-full">
                            <InputIcon v-if="!phone_number" class="pi pi-phone"> </InputIcon>
                            <InputIcon v-else class="pi pi-times pr-2 cursor-pointer" @click="clear_phone()"> </InputIcon>
                        
                            <InputText v-if="phone_mask_arr.length>1" input-id="phone_number" v-model="phone_number" :placeholder="get_mask(phone_mask_arr[0])" aria-labelledby="phone" aria-describedby="phone-help"
                                class="w-full p-inputtext-lg border-2 border-round-right-lg border-noround-left" />

                            <InputMask v-else input-id="phone_number" v-model="phone_number" :mask="get_mask(phone_mask_arr[0])" :placeholder="get_mask(phone_mask_arr[0])"
                                class="w-full p-inputtext-lg border-2 border-round-right-lg border-noround-left"/>

                        </IconField>
                    </div>
                </div>
                <small v-if="!phone_is_valid && submitted" class="p-error">Phone is not valid</small>
            </div>

            <!--TELEGRAM-->
            <div v-if="showTg" class="field my-3">
                <IconField iconPosition="right" class="w-full">
                    <InputText input-id="telegram" type="text" v-model="telegram" aria-describedby="name-help"
                        :placeholder="content[lang].tg_placeholder"
                        class="w-full p-inputtext-lg border-2 border-round-lg surface-border" />
                    <InputIcon class="pi pi-telegram mr-2"> </InputIcon>
                </IconField>
            </div>
        </div>

        <!--DESCRIPTION-->
        <div class="w-full mb-1">
            <div v-if="!hide_desc" class="mb-3">
                <span class="text-color-secondary">{{ content[lang].description_title }}:</span>
                <Textarea rows="5" cols="20" class="w-full surface-border border-2 border-round-xl text-sm p-2 mt-2 mb-1" 
                    :value="description" :placeholder="content[lang].description_placeholder"
                    @input="description = $event.target.value"
                    style="resize: none !important; font-family: inherit;"
                    />

                <Message severity="warn" class="border-round-xl">
                    <span class="line-height-2 text-xs xl:text-md lg:text-sm">
                        {{ content[lang].description_msg }}
                    </span>
                </Message>
            </div>

            <div v-if="hide_desc" class="w-full mb-3">
                <span class="cursor-pointer text-primary" @click="showDesc(true)">
                    <span class="mr-2">{{ content[lang].show_desc }}</span>
                    <i class="pi pi-pen-to-square text-600 mr-2 text-primary"></i>
                </span>
            </div>

            <div v-if="!hide_desc" class="w-full mb-3">
                <span class="cursor-pointer text-primary" @click="showDesc(false)">
                    <span class="mr-2">{{ content[lang].hide_desc }}</span>
                    <i class="pi pi-replay text-600 mr-2 text-primary"></i>
                </span>
            </div>
        </div>

        <div class="field w-full">
            <Button @click="send()" :label="content[lang].send" class="p-button-lg w-full border-round-2xl" :loading="loading" :disabled="!valid || loading" severity="info" />
        </div>

    </div>

    <Dialog v-model:visible="popup" modal
        class="w-full border-round-xl overflow-hidden border-none surface-100"
        :position="screenSize=='xs' ? 'bottom' : 'center'"
        :style="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? {width: '100%!important', margin: '0!important', padding: '0!important'} : { width: 'auto', height: '100%', }" 
        :contentStyle="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? { height: '100%' } : { height: '100%' }"
        :class="(screenSize=='xs') ? 'border-noround-bottom h-full' : 'max-w-30rem'">

        <template #header>
            <span class="text-lg font-medium">{{ content[lang].currency_receive_title }}</span>
        </template>

        <div class="w-full flex flex-column gap-2" style="min-height:300px">
            <div v-if="currencies && currencies.length" class="flex flex-column gap-3 xl:py-4 xl:px-1 lg:py-3 md:py-2">
                <MobMenu>
                    <template v-slot:body>
                        <div v-for="curr of filteredCurrencies" @click="currency_receive=curr; popup=false" :key="curr.code"
                            class="w-full relative flex flex-row gap-2 px-3 py-2 border-100 cursor-pointer border-bottom-1" style="min-height:65px">
                            <div class="flex flex-row flex-1 gap-3">
                                <div v-if="curr.image" class="flex align-items-center">
                                    <AvatarGroup>
                                        <Avatar :image="getCurImage(curr)" class="w-full surface-0 border-100" shape="circle"/>
                                    </AvatarGroup>
                                </div>
                                <div class="flex-1 flex flex-column gap-1 justify-content-center">
                                    <div class="w-full flex flex-column gap-1 overflow-hidden white-space-nowrap align-items-start font-medium text-sm xl:text-md" >
                                        <span class="uppercase">
                                            {{ curr.code }}
                                        </span>
                                        <span v-if="curr.titles && curr.titles[lang] && curr.sign" class="text-500">
                                            {{ curr.titles[lang] }}, {{ curr.sign }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="absolute right-0 top-0 bottom-0 flex justify-content-center align-items-center mr-3">
                                <div class="surface-border border-1 border-circle surface-0 deep_white_shadow">
                                    <div class="border-circle" style="width:18px;height:18px;margin:3px" :class="{'bg-green-400 surface-0': curr.code==currency_receive.code}"></div>
                                </div>
                            </div>
                        </div>
                    </template>
                </MobMenu>
            </div>
        </div>

        <template #footer>
            <div class="w-full flex pt-3">
                <InputGroup class="w-full">
                    <InputText v-model="searchStr" size="large" :placeholder="content[lang].search_placeholder"
                        class="w-full flex border-2 border-300 border-round-left-xl border-right-none" />
                    <InputGroupAddon class="border-2 border-300 border-round-right-xl border-left-none">
                        <i class="pi pi-search"></i>
                    </InputGroupAddon>
                </InputGroup>
            </div>
        </template>
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import Event from '@/assets/js/Event.js'
import { CurrencyList } from '@/assets/js/classes/Currency.js'
import InputAmount from '@/components/widgets/transfer/InputAmount.vue'
import MobMenu from '@/components/sides/menu/MobMenu.vue'

export default {
    props: ['transferError', 'errorMessage'],
    data() {
        return {
            amount_send: null,
            currency_send: null,
            currency_receive: null,
            currencies: [],
            telegram: null,
            phone_number: null,
            typesContact: [
                { label: 'Telegram', value: 'telegram', icon: 'pi pi-telegram' },
                { label: 'WhatsApp', value: 'whatsapp', icon: 'pi pi-whatsapp' },
            ],
            contact: { label: 'Telegram', value: 'telegram', icon: 'pi pi-telegram' },
            selectedCountry: null,
            countries: [],
            phone_mask_arr: ['(999)999-99-99'],
            phone_is_valid: false,
            description: null,
            hide_desc: true,
            popup: false,
            searchStr: '',

            loading: false,
            totalError: false,
            totalErrorMessage: null,

            content: {
                ru: {
                    inline_msg_info: "Укажите желаемую сумму и валюту для отправки. А также валюту получения. Наш специалист свяжется с вами и организует перевод в кратчайшие сроки. ",
                    inline_msg_nomerchants: 'Кажется сейчас некому выполнить ваш запрос. Попробуйте обновить страницу позже или оставьте заявку и мы свяжемся с вами в ближайшее время.',
                    inline_msg_norates: 'Кажется сейчас нет курсов валют. Попробуйте обновить страницу позже или оставьте заявку и мы свяжемся с вами в ближайшее время.',
                    amount_title: "Сумма и валюта отправки",
                    amount_placeholder: "Сумма",
                    currency_receive_title: "Валюта получения",
                    currency_send_placeholder: "Валюта отправки",
                    currency_receive_placeholder: "Валюта получения",
                    contact: "Как с вами связаться",
                    tg_placeholder: "Ваш ник в Telegram",
                    country_placeholder: "Выберите страну",
                    description_title: "Описание",
                    description_placeholder: "Опишите ваш запрос",
                    description_msg: "Укажите дополнительную информацию: банк отправителя и получателя, страну и другие особенности перевода.",
                    show_desc: "Показать описание",
                    hide_desc: "Скрыть описание",
                    send: "Отправить запрос",
                    success: 'Успешно',
                    sent: 'Ваш запрос отправлен',
                    error: 'Ошибка',
                    notsent: 'Ваш запрос не отправлен',
                    search_placeholder: "Поиск",
                },
                en: {
                    inline_msg_info: "Specify the desired amount and currency for sending. And also receiving currency. Our specialist contacts you, and the central transfer is transferred as soon as possible.",
                    inline_msg_nomerchants: 'It seems that there is no one to fulfill your request right now. Try refreshing the page later or leave a request and we will contact you shortly.',
                    inline_msg_norates: 'It seems that there are no exchange rates right now. Try refreshing the page later or leave a request and we will contact you shortly.',
                    amount_title: "Amount and currency for sending",
                    amount_placeholder: "Amount",
                    currency_receive_title: "Currency receive",
                    currency_send_placeholder: "Currency send",
                    currency_receive_placeholder: "Currency receive",
                    contact: "How to contact you",
                    tg_placeholder: "Your username in Telegram",
                    country_placeholder: "Select country",
                    description_title: "Description",
                    description_placeholder: "Describe your request",
                    description_msg: "Specify additional information: senders's and recipient`s banks, country and other transfer features.",
                    show_desc: "Show description",
                    hide_desc: "Hide description",
                    send: "Send request",
                    success: 'Success',
                    sent: 'Your request has been sent',
                    error: 'Error',
                    notsent: 'Your request has not been sent',
                    search_placeholder: "search",
                },
            }
        }
    },
    components: {
        InputAmount,
        MobMenu,
    },
    computed: {
        isAuth: function () { return this.$store.getters.isAuthenticated },
        lang() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        screenSize() { return this.$store.getters.StateScreenSize },
        showWa(){
            return this.contact && this.contact.value && this.contact.value == 'whatsapp'
        },
        showTg(){
            return this.contact && this.contact.value && this.contact.value == 'telegram'
        },
        isTelegramOk(){
            return this.telegram && this.telegram.length > 0
        },
        isPhoneOk(){
            return this.phone_is_valid
        },
        valid(){
            if(!this.amount_send || !this.currency_send || !this.currency_receive) return false
            if(!this.isAuth) {
                if(this.showTg && !this.isTelegramOk) return false
                if(this.showWa && !this.isPhoneOk) return false
            }
            return true
        },
        filteredCurrencies() {
            if (!this.searchStr || this.searchStr.trim() === '') {
                return this.currencies
            }
            
            const search = this.searchStr.toLowerCase().trim()
            
            return this.currencies.filter(curr => {
                const code = curr.code ? curr.code.toLowerCase() : ''
                const name = curr.titles && curr.titles[this.lang] ? curr.titles[this.lang].toLowerCase() : ''
                const sign = curr.sign ? curr.sign.toLowerCase() : ''
                
                return code.includes(search) || name.includes(search) || sign.includes(search)
            })
        },
    },
    watch: {
        contact(){
            if(this.showWa) this.phone_number = ''
            if(this.showTg) this.telegram = ''
        },
        selectedCountry(){
            try{
                if(this.selectedCountry.mask && this.selectedCountry.mask.length > 0) {
                    if(typeof(this.selectedCountry.mask)=='object'){
                        this.phone_mask_arr = this.selectedCountry.mask
                    }else{
                        this.phone_mask_arr = [this.selectedCountry.mask]
                    }
                }else{
                    this.phone_mask_arr = []
                }
            } catch (error) {
                this.phone_mask_arr = []
            }
        },
        phone_number(){
            if(this.phone_number && this.phone_number.length > 0){
                let phone = this.phone_number.replace(/\D/g, '')
                if(phone.length < 5) {
                    this.phone_is_valid = false
                    return
                }
                this.phone_is_valid = true
            }else{
                this.phone_is_valid = false
            }
        }
    },
    methods: {
        getCurFlag(cur) {
            return cur.flag ? cur.flag : '-'
        },
        getCurCode(cur, up = false) {
            return cur.code ? (up ? cur.code.toUpperCase() : cur.code) : '-'
        },
        getCurName(cur) {
            return cur.name ? cur.name : '-'
        },
        getPrefix(country){
            return country.first ? country.first : ''
        },
        clear_phone(){
            this.phone_is_valid = false
            this.phone_number = ''
        },
        get_mask(val){
            return val.replaceAll('\#', '9')
        },
        getFirstCountry(countries, code='RU'){
            for (let i = 0; i < countries.length; i++) {
                if(countries[i].code == code){
                    this.selectedCountry = countries[i]
                    break
                }
            }
            if(!this.selectedCountry) this.selectedCountry = countries[0]
        },
        showDesc(val){
            this.hide_desc = !val
            if(!val) this.description = null
        },
        showMess(severity, summary, detail) {
            this.$toast.add({ severity: severity, summary: summary, detail: detail, life: 3000 });
        },
        async send(){
            if(!this.valid) return
            this.loading = true
            let phone = (this.phone_number && typeof(this.phone_number) == 'string') ? this.phone_number.replace(/\D/g, '') : ''
            let message = {
                amount_send: this.amount_send,
                currency_send: this.currency_send.code,
                currency_receive: this.currency_receive.code,
                contact: this.contact.value,
                prefix: this.getPrefix(this.selectedCountry),
                phone_number: phone,
                country: this.getCurCode(this.selectedCountry),
                telegram: this.telegram,
                description: this.description,
            }

            const from = 'request'
            let send

            try{
                if(this.isAuth) send = await this.connect.sendMessage(message, from, []).then(res => res)
                else            send = await this.connect.sendPublicMessage(message, from).then(res => res)
                if(send.result) {
                    this.showMess('success', this.content[this.lang].success, this.content[this.lang].sent)
                    this.clear()
                    this.event.send('requestSent')
                } else {
                    this.showMess('error', this.content[this.lang].error, this.content[this.lang].notsent)
                }

                this.loading = false
                return
            } catch (error) {
                this.showMess('error', this.content[this.lang].error, 'Error sending message')
                this.loading = false
                return
            }
        },
        clear(){
            this.amount_send = null
            this.currency_send = this.currencies[0]
            this.currency_receive = this.currencies[1]
            this.contact = { label: 'Telegram', value: 'telegram', icon: 'pi pi-telegram' }
            this.selectedCountry = this.countries[0]
            this.phone_number = null
            this.telegram = null
            this.description = null
            this.hide_desc = true
        },
        getTextMsg(msg){
            switch (msg) {
                case 'Error getting all merchs': return this.content[this.lang].inline_msg_nomerchants
                case 'No rates loaded': return this.content[this.lang].inline_msg_norates
                default: return msg
            }
        },
        getCurImage(cur){
            if(!cur || !cur.image) return ''
            return cur.image
        },
        async LoadCurrencies(){
            const resCurrencies = await CurrencyList.GetList(this.$store).then(r=>r)
            if (resCurrencies || resCurrencies.length <= 0){
                this.currencies = resCurrencies
                this.currency_send = this.currencies[0]
                this.currency_receive = this.currencies[1]
            } else {
                this.totalError = true
                this.totalErrorMessage = 'No currencies loaded'
                return
            }
        }
    },
    async beforeCreate(){
        this.connect = new Connect(this)
        this.helper = new Helper()
        this.event = new Event(this)
    },
    async mounted() {
        await this.LoadCurrencies().then(r=>r)

        // страны | обязательно
        const storeCountries = this.$store.getters.GetCountries
        if(storeCountries && Object.keys(storeCountries).length) this.countries = storeCountries
        else {
            const resCountries = await this.$store.dispatch('GetCountries').then(res => res)
            if (!resCountries.result || !resCountries.data || resCountries.data.length <= 0){
                this.totalError = true
                this.totalErrorMessage = 'No countries loaded'
                return
            }
            this.countries = resCountries.data
        }
        this.getFirstCountry(this.countries, 'RU')
    }
}
</script>